/**
 * App — application shell.
 *
 * Renders the site chrome (header / footer) around the routed screen. The live
 * overview is the home screen (`/`); the per-hospital detail screen (history
 * chart + rough estimated wait + the day×hour congestion heatmap, all inline)
 * lives at `/ps/:slug`. The collector-status diagnostics page is at `/status`
 * (also surfaced as the footer indicator).
 *
 * `/ps/:slug/heatmap` redirects to the detail screen — the heatmap is inline
 * there, and the redirect keeps external links working.
 */
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import UmamiAnalytics from "@danielgtmn/umami-react";
import OverviewPage from "@/pages/OverviewPage";
import HospitalDetailPage from "@/pages/HospitalDetailPage";
import StatusPage from "@/pages/StatusPage";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ScrollManager } from "@/components/ScrollManager";

/**
 * The Umami tracker belongs only on the public site. Loading it on a local,
 * staging, or E2E origin neither records a pageview (the tracker is restricted
 * to the public domain) nor helps the application — it only adds an unrelated
 * external network request to those environments.
 */
const isPublicSite =
  typeof window !== "undefined" && window.location.hostname === "pspulse.frikyfriky11.dev";

/** Legacy heatmap URL → the detail screen, where the heatmap now lives inline. */
function HeatmapRedirect() {
  const { slug = "" } = useParams<{ slug: string }>();
  return <Navigate to={`/ps/${slug}`} replace />;
}

export default function App() {
  return (
    <div className="layout">
      {/*
        Scroll handling for the non-morph navigation paths (reduced motion,
        unsupported View Transitions, browser back/forward). Renders nothing.
        The morph path resets/restores scroll itself — see lib/morph.ts. #102
      */}
      <ScrollManager />
      {/*
        Privacy-friendly, cookieless analytics (self-hosted Umami on the homelab).
        Renders nothing — just injects the tracker. `onlyInProduction` (default)
        keeps it out of `pnpm dev`; `domains` makes Umami's own script ignore any
        events that don't originate from the real production host (prod builds run
        locally, in `vite preview`, or in CI never pollute the stats). Umami patches
        history.pushState, so react-router navigations auto-fire pageviews.
      */}
      {isPublicSite && (
        <UmamiAnalytics
          url="https://umami.frikyfriky11.dev"
          websiteId="b3c03319-6adf-45dc-9ee9-0dd9b66f7b79"
          domains={["pspulse.frikyfriky11.dev"]}
        />
      )}
      <SiteHeader />
      <ErrorBoundary>
        <Routes>
          <Route path="/" element={<OverviewPage />} />
          <Route path="/ps/:slug" element={<HospitalDetailPage />} />
          <Route path="/ps/:slug/heatmap" element={<HeatmapRedirect />} />
          <Route path="/status" element={<StatusPage />} />
          {/* Unknown paths fall back to the overview. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ErrorBoundary>
      <SiteFooter />
    </div>
  );
}
